import { useState, useCallback } from 'react'
import { X, Download, FileText } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { Button } from '../../ui/button'
import { Slider } from '../../ui/slider'
import PostGenerateWordCloud from '../../../core/api/services/bookmetrie/private-reports/chroniqueur/post-generate-word-cloud.service'
import PostSaveWordCloud from '../../../core/api/services/bookmetrie/private-reports/chroniqueur/post-save-word-cloud.service'
import { Input } from '../../ui/input'

const WordCloudModal = ({
  url,
  onClose,
  setWordCloudUrl,
  idEan,
}: {
  url: string | null
  onClose: () => void
  setWordCloudUrl: (url: string | null) => void
  idEan?: string
}) => {
  const { t } = useTranslation()
  const [adjectiveWeight, setAdjectiveWeight] = useState<number[]>([10])
  const [excludedWords, setExcludedWords] = useState<string>('')
  const [isGenerating, setIsGenerating] = useState(false)

  const handleGenerate = useCallback(async () => {
    if (!idEan) return

    setIsGenerating(true)
    try {
      const response = await PostGenerateWordCloud(
        excludedWords,
        idEan,
        false,
        adjectiveWeight[0],
      )

      if (response.data?.word_cloud_url) {
        const newUrl = `${
          response.data.word_cloud_url
        }?t=${new Date().getTime()}`
        setWordCloudUrl(newUrl)
      }
    } catch {
      toast.error(t('chroniqueurView.wordCloud.generateError'))
    } finally {
      setIsGenerating(false)
    }
  }, [excludedWords, idEan, adjectiveWeight])

  const handleDownload = useCallback(() => {
    if (url) {
      const link = document.createElement('a')
      link.href = url
      link.download = 'word-cloud.png'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }, [url])

  const handleSave = useCallback(() => {
    PostSaveWordCloud(idEan || '', url || '')
      .then(() => {
        onClose()
        toast.success(t('chroniqueurView.wordCloud.savedSuccess'))
      })
      .catch(() => {
        toast.error(t('chroniqueurView.wordCloud.saveError'))
      })
  }, [url, idEan])

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-fit w-fit p-6">
        {/* Header */}
        <div className="flex justify-between items-center border-b p-4">
          <h2 className="text-lg font-semibold">
            {t('chroniqueurView.wordCloud.generate')}
          </h2>
          <Button variant="ghost" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex">
          {/* Left side - Word Cloud */}
          <div className="flex-1 p-4 relative ">
            {url ? (
              <div className="relative h-full flex ">
                <img
                  src={url}
                  alt="Word Cloud"
                  className="w-[576px] h-[300px] object-contain rounded-lg"
                />
                {/* Download button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownload}
                  className="top-0 right-0 bg-white/90 hover:bg-white"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <FileText className="h-16 w-16 mb-4" />
                <p>{t('chroniqueurView.wordCloud.notAvailable')}</p>
              </div>
            )}
          </div>

          {/* Right side - Controls */}
          <div className="flex flex-col justify-center gap-6 p-4">
            {/* Adjective weight slider */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium">
                  {t('chroniqueurView.wordCloud.ponderateAdjectives')}
                </h3>
              </div>
              <div className="space-y-2">
                <Slider
                  value={adjectiveWeight}
                  onValueChange={setAdjectiveWeight}
                  max={100}
                  min={0}
                  step={1}
                  className="w-full"
                />
                <div className="text-center text-lg font-medium">
                  {adjectiveWeight[0]}
                </div>
              </div>
            </div>

            {/* Excluded words */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium">
                  {t('chroniqueurView.wordCloud.excludeWords')}
                </h3>
              </div>
              <div className="relative">
                <Input
                  value={excludedWords}
                  onChange={(e) => setExcludedWords(e.target.value)}
                  placeholder="mot1;mot2;mot3"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-1 right-1 p-1"
                  onClick={() => setExcludedWords('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer buttons */}
        <div className="flex justify-center gap-4 p-4 border-t">
          <Button
            onClick={() => void handleGenerate()}
            disabled={isGenerating || !idEan}
            className="bg-black text-white hover:bg-gray-800 px-8"
          >
            {isGenerating
              ? t('chroniqueurView.loading')
              : t('chroniqueurView.generate')}
          </Button>
          <Button
            variant="outline"
            onClick={handleSave}
            disabled={!url}
            className="px-8"
          >
            {t('chroniqueurView.save')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default WordCloudModal
